/**
 * Anonymous usage counts, only when the site owner turned them on in the dashboard. The widget
 * counts, per page view and at most once each, that the panel was opened and which features or
 * profiles a visitor turned on, and sends just those names when the page is hidden. No cookie, no
 * identifier, no page address, no referrer, no settings values: the server keeps daily totals per
 * site and nothing per visitor.
 */

export type StatsEvent = 'open' | `feature:${string}` | `profile:${string}`;

export interface StatsReporter {
  record(event: StatsEvent): void;
  /** Sends what has not been sent yet; called on its own when the page is hidden. */
  flush(): void;
  destroy(): void;
}

const EVENT = /^(open|(feature|profile):[a-z][a-z0-9-]{0,39})$/;
const MAX_EVENTS = 40;

export function statsUrl(siteKey: string, apiBase: string): string | null {
  try {
    const base = apiBase.endsWith('/') ? apiBase : `${apiBase}/`;
    return new URL(`v1/sites/${encodeURIComponent(siteKey)}/stats`, base).href;
  } catch {
    return null;
  }
}

export function createStatsReporter(input: { siteKey: string; apiBase: string; win: Window; fetch?: typeof fetch }): StatsReporter {
  const url = statsUrl(input.siteKey, input.apiBase);
  const fetchImpl = input.fetch ?? (typeof input.win.fetch === 'function' ? input.win.fetch.bind(input.win) : undefined);
  const seen = new Set<string>();
  let pending: string[] = [];

  const flush = (): void => {
    if (!url || !fetchImpl || pending.length === 0) return;
    const events = pending;
    pending = [];
    try {
      // keepalive lets the request finish after the page goes away; no-referrer keeps the page
      // address out of it, and omit keeps cookies out.
      void fetchImpl(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        keepalive: true,
        headers: { 'content-type': 'text/plain' },
        body: JSON.stringify({ v: 1, events }),
      }).catch(() => undefined);
    } catch {
      // Counting must never break the widget.
    }
  };

  const onHide = (): void => {
    if (input.win.document.visibilityState === 'hidden') flush();
  };
  input.win.addEventListener('pagehide', flush);
  input.win.document.addEventListener('visibilitychange', onHide);

  return {
    record: (event) => {
      if (!EVENT.test(event) || seen.has(event) || seen.size >= MAX_EVENTS) return;
      seen.add(event);
      pending.push(event);
    },
    flush,
    destroy: () => {
      flush();
      input.win.removeEventListener('pagehide', flush);
      input.win.document.removeEventListener('visibilitychange', onHide);
    },
  };
}

/**
 * The events a settings change adds: a newly chosen profile (its features count as the profile,
 * not as choices of their own), or else the features newly turned on.
 */
export function eventsFromChange(
  before: { features: Record<string, number>; profile: string | null },
  after: { features: Record<string, number>; profile: string | null },
): StatsEvent[] {
  if (after.profile && after.profile !== before.profile) return [`profile:${after.profile}`];
  return Object.keys(after.features)
    .filter((id) => !(before.features[id] ?? 0) && (after.features[id] ?? 0) > 0)
    .map((id): StatsEvent => `feature:${id}`);
}
